import { Card, FormField, FormFieldRow, Heading, ReadOnlyField } from '@msaf/core-react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { InputWidth } from '../../../../types/aoraki/input-width-types'
import { FormFieldsView } from '../../../../types/form-types'

interface WellHeadConstructionViewInterface {
  fields: Partial<FormFieldsView>
  isLoading: boolean
}

export default function WellHeadConstructionView({ fields = {}, isLoading }: WellHeadConstructionViewInterface) {
  return (
    <>
      <FormField
        toolTipMessage={TOOLTIP.casingHeight}
        isSkeleton={isLoading}
        label='Ground level to top of casing (m)'
        labelId='casingHeightLabel'
      >
        <ReadOnlyField
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          labelledBy='casingHeightLabel'
          value={`${fields.casingHeight ?? ''}`}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.elevationRefPointDescription}
        isSkeleton={isLoading}
        label='Elevation reference point description'
        labelId='elevationRefPointDescriptionLabel'
      >
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='elevationRefPointDescriptionLabel'
          value={`${fields.elevationRefPointDescription ?? ''}`}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.groundLevelCorrection}
        isSkeleton={isLoading}
        label='Ground level correction (m)'
        labelId='groundLevelCorrectionLabel'
      >
        <ReadOnlyField
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          labelledBy='groundLevelCorrectionLabel'
          value={`${fields.groundLevelCorrection ?? ''}`}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.measuringPointDescription}
        isSkeleton={isLoading}
        label='GWL measuring point description'
        labelId='measuringPointDescriptionLabel'
      >
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='measuringPointDescriptionLabel'
          value={`${fields.measuringPointDescription ?? ''}`}
        />
      </FormField>

      <FormField
        toolTipMessage={TOOLTIP.measuringPointCorrection}
        isSkeleton={isLoading}
        label='GWL measuring point correction (m)'
        labelId='measuringPointCorrectionLabel'
      >
        <ReadOnlyField
          fieldSize={InputWidth.medium}
          isSkeleton={isLoading}
          labelledBy='measuringPointCorrectionLabel'
          value={`${fields.measuringPointCorrection ?? ''}`}
        />
      </FormField>

      <Card>
        <Heading isSkeleton={isLoading} level={4}>
          New Zealand Vertical Datum 2016
        </Heading>

        <FormFieldRow>
          <FormField
            toolTipMessage={TOOLTIP.measuringPointElevationNzvd2016}
            isSkeleton={isLoading}
            label='Elevation'
            labelId='nzvdElevationLabel'
          >
            <ReadOnlyField
              isSkeleton={isLoading}
              labelledBy='nzvdElevationLabel'
              value={`${fields.nzvdElevation ?? ''}`}
            />
          </FormField>

          <FormField isSkeleton={isLoading} label='Elevation accuracy' labelId='nzvdElevationAccuracyLabel'>
            <ReadOnlyField
              isSkeleton={isLoading}
              labelledBy='nzvdElevationAccuracyLabel'
              value={`${fields.nzvdElevationAccuracy ?? ''}`}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            toolTipMessage={TOOLTIP.measuringPointLocalMethodId}
            isSkeleton={isLoading}
            label='Method'
            labelId='nzvdMethodLabel'
          >
            <ReadOnlyField isSkeleton={isLoading} labelledBy='nzvdMethodLabel' value={`${fields.nzvdMethod ?? ''}`} />
          </FormField>

          <FormField
            toolTipMessage={TOOLTIP.measuringPointNzvdDate}
            isSkeleton={isLoading}
            label='Date'
            labelId='nzvdDateLabel'
          >
            <ReadOnlyField isSkeleton={isLoading} labelledBy='nzvdDateLabel' value={`${fields.nzvdDate ?? ''}`} />
          </FormField>
        </FormFieldRow>

        <FormField
          toolTipMessage={TOOLTIP.measuringPointNzvdSurveyBy}
          isSkeleton={isLoading}
          label='Surveyed by'
          labelId='nzvdSurveyedByLabel'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='nzvdSurveyedByLabel'
            value={`${fields.measuringPointNzvdSurveyBy ?? ''}`}
          />
        </FormField>
      </Card>

      <Card>
        <Heading isSkeleton={isLoading} level={4}>
          Local datum
        </Heading>
        <FormField isSkeleton={isLoading} label='Datum type' labelId='localDatumLabel'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='localDatumLabel' value={`${fields.localDatum ?? ''}`} />
        </FormField>
        <FormFieldRow>
          <FormField
            toolTipMessage={TOOLTIP.measuringPointLocalElevation}
            isSkeleton={isLoading}
            label='Elevation'
            labelId='localElevationLabel'
          >
            <ReadOnlyField
              isSkeleton={isLoading}
              labelledBy='localElevationLabel'
              value={`${fields.localElevation ?? ''}`}
            />
          </FormField>

          <FormField isSkeleton={isLoading} label='Elevation accuracy' labelId='localElevationAccuracyLabel'>
            <ReadOnlyField
              isSkeleton={isLoading}
              labelledBy='localElevationAccuracyLabel'
              value={`${fields.localElevationAccuracy ?? ''}`}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField isSkeleton={isLoading} label='Method' labelId='localMethodLabel'>
            <ReadOnlyField isSkeleton={isLoading} labelledBy='localMethodLabel' value={`${fields.localMethod ?? ''}`} />
          </FormField>

          <FormField
            toolTipMessage={TOOLTIP.measuringPointLocalDate}
            isSkeleton={isLoading}
            label='Date'
            labelId='localDateLabel'
          >
            <ReadOnlyField isSkeleton={isLoading} labelledBy='localDateLabel' value={`${fields.localDate ?? ''}`} />
          </FormField>
        </FormFieldRow>

        <FormField
          toolTipMessage={TOOLTIP.measuringPointLocalSurveyBy}
          isSkeleton={isLoading}
          label='Surveyed by'
          labelId='measuringPointLocalSurveyByLabel'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='measuringPointLocalSurveyByLabel'
            value={`${fields.measuringPointLocalSurveyBy ?? ''}`}
          />
        </FormField>
      </Card>
    </>
  )
}
